import React from "react";

import { useBackDismissesOverlay } from "@/main/navigation/useBackDismissesOverlay";
import MediaLightboxLazy from "@/main/react/media/MediaLightboxLazy";
import { FULL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";
import { trackThumbnailZoomOpened, type ThumbnailZoomSite } from "@/main/analytics/analyticsEvents";
import type { Localizer } from "@/main/react/Types";

/**
 * Opens the full-screen viewer over a TERMINAL entity thumbnail — the avatar on a profile page, the
 * group tile on a group page, the series tile on a series page.
 *
 * <p><b>Why terminal only.</b> A thumbnail has exactly two jobs and which one it has is decided by
 * where the member already is: on a card or a byline it is the entity's handle and must navigate; on
 * the entity's own page there is nowhere to navigate to, so today it is an inert control, which is
 * worse than either job. Design:
 * `designs/00-inbox/2026-09-19-terminal-thumbnails-zoom-instead-of-navigating.md`.
 *
 * <p><b>The one-image sibling of {@link useBodyMediaLightbox}.</b> That hook enhances injected body
 * HTML, collects a gallery and intercepts anchor clicks; this one is handed a single URL by a React
 * call site and has no DOM pass at all. What the two DO share is the history bookkeeping, and that is
 * the whole reason this file exists rather than eight lines inlined at four call sites: the marker
 * written on open and the marker read back before the pop must be the same string, and a copy per call
 * site is four places for it to drift. A close that reads a marker the open never wrote refuses every
 * pop silently and looks exactly like a lightbox that never armed.
 *
 * <p><b>Back closes the overlay (AGENTS.md rule 33), and the lifecycle is DELEGATED.</b>
 * `useBackDismissesOverlay` owns the whole of it: one stamped sacrificial entry on the closed→open
 * edge, the pop guard the router consults, the self-pop bookkeeping, the departure observer and the
 * unmount cleanup. The address bar is deliberately untouched — the viewer is a view state of the page
 * it opened from, not a route.
 *
 * <p><b>This file hand-rolled that lifecycle until 2026-09-20 (FA-11), and lost two behaviours by
 * doing so</b> — the soft-navigation departure observer (2026-08-27) and the unmount-time pop of an
 * orphaned guard entry, both added to the shared hook AFTER the copy was taken. That is the whole
 * argument for delegating: a copy of a lifecycle is a copy of its bug history frozen at the moment of
 * copying, and nothing makes it move again.
 *
 * <p><b>Full size, not medium (decision A1).</b> The thumbnail the call site holds is the `'t'` (400px)
 * variant; full-screening that is a blur. `FULL_IMAGE_SIZE` (`'f'`, ≤3200px) is the top of the one
 * shared variant ladder, the resizer never enlarges past the original, and the client never uploads
 * below a 1024px long edge — so `'f'` is the real picture, not an upscale.
 */

/** Stamped onto the sacrificial entry and read back before it is popped. One constant, two readers. */
const THUMBNAIL_LIGHTBOX_GUARD_MARKER = "seavienThumbnailLightbox";

export type UseThumbnailLightboxOptions = {
    localizer: Localizer;
    /**
     * Which terminal site hosts this viewer. It is the ENTIRE payload of the `thumbnail_zoom_opened`
     * analytics event (`FI-344`): no image URL, no username, no id.
     */
    site: ThumbnailZoomSite;
};

export type UseThumbnailLightboxResult = {
    /**
     * Pass as the thumbnail's `onClick`. Call it with the image URL the call site is rendering, which
     * is not always the URL it was mounted with — the profile avatar is re-pointed in place when the
     * member changes their picture, and zooming the previous one is a bug nothing would report.
     *
     * <p>A blank or absent URL is a no-op: there is nothing to open, and `ProfileImage` withholds the
     * control entirely for a letter tile, so this is defence in depth rather than the mechanism.
     */
    openZoom: (imageUrl?: string | null, alt?: string | null) => void;
    /** Render anywhere inside the host; `null` until a thumbnail is clicked. */
    lightbox: React.ReactNode;
};

type OpenState = {
    src: string;
    alt: string;
};

export function useThumbnailLightbox({ localizer, site }: UseThumbnailLightboxOptions): UseThumbnailLightboxResult {
    const [openState, setOpenState] = React.useState<OpenState | null>(null);

    const isOpen = openState !== null;

    const closeOverlay = React.useCallback(() => {
        setOpenState(null);
    }, []);

    // Every dismissal path — close button, Escape, backdrop, Back, a soft navigation out, unmount —
    // goes through this one boolean. The hook arms on the closed→open EDGE, which also fixes a defect
    // the hand-rolled version had: re-pointing the zoom at a second image while it was already open
    // pushed a SECOND guard entry, and only the first was ever consumed.
    useBackDismissesOverlay({
        open: isOpen,
        onRequestClose: closeOverlay,
        marker: THUMBNAIL_LIGHTBOX_GUARD_MARKER,
    });

    // `FI-344` — one analytics event per closed→open EDGE. Keyed on the BOOLEAN, never on `openState`:
    // a re-point while open changes `openState`'s identity but not whether the viewer is open, and must
    // not count as a second open. The site is read through a ref so a changed option cannot re-arm it.
    const siteRef = React.useRef(site);
    siteRef.current = site;
    React.useEffect(() => {
        if (isOpen) {
            trackThumbnailZoomOpened(siteRef.current);
        }
    }, [isOpen]);

    const openZoom = React.useCallback((imageUrl?: string | null, alt?: string | null) => {
        const src = imageUrl?.trim();
        if (!src) {
            return;
        }
        setOpenState({ src, alt: alt?.trim() || "" });
    }, []);

    const lightbox = openState
        ? (
            <MediaLightboxLazy
                items={[{ src: openState.src, alt: openState.alt }]}
                initialIndex={0}
                localizer={localizer}
                onClose={closeOverlay}
                imageSize={FULL_IMAGE_SIZE}
            />
        )
        : null;

    return { openZoom, lightbox };
}
